import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userPreferencesService } from 'src/data/api/orbit-service/userPreferencesService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  UserPreferencesDetails,
  GetUserPreferencesRequest,
  UpdateUserPreferencesRequest,
} from 'src/types/orbit/UserPreferences';
import { handleError } from 'src/utils/generalUtils';

export interface UserPreferencesState {
  userPreferences?: UserPreferencesDetails;
  getUserPreferencesApiStatus: ApiRequestStatus;
  updateUserPreferencesApiStatus: ApiRequestStatus;
}

const initialState: UserPreferencesState = {
  getUserPreferencesApiStatus: initApiStatus,
  updateUserPreferencesApiStatus: initApiStatus,
};

export const getUserPreferencesApiThunk = createAsyncThunk(
  'userPreferences/getUserPreferences',
  async (data: GetUserPreferencesRequest, { rejectWithValue }) => {
    try {
      const response = await userPreferencesService.get(data);
      return response.preferences;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateUserPreferencesApiThunk = createAsyncThunk(
  'userPreferences/updateUserPreferences',
  async (data: UpdateUserPreferencesRequest, { rejectWithValue }) => {
    try {
      const response = await userPreferencesService.update(data);
      return {
        preferences: response.preferences,
        message: 'User preferences updated successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    clearUserPreferencesMessages: (state) => {
      state.updateUserPreferencesApiStatus.errorMessage = '';
      state.updateUserPreferencesApiStatus.successMessage = '';
    },
    resetUpdateApiStatus: (state) => {
      state.updateUserPreferencesApiStatus.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserPreferencesApiThunk.pending, (state) => {
        state.getUserPreferencesApiStatus.isLoading = true;
        state.getUserPreferencesApiStatus.errorMessage = null;
      })
      .addCase(getUserPreferencesApiThunk.fulfilled, (state, action) => {
        state.getUserPreferencesApiStatus.isLoading = false;
        state.userPreferences = action.payload;
      })
      .addCase(getUserPreferencesApiThunk.rejected, (state, action) => {
        state.getUserPreferencesApiStatus.isLoading = false;
        state.userPreferences = undefined;
        state.getUserPreferencesApiStatus.errorMessage = action.payload as string;
      })

      .addCase(updateUserPreferencesApiThunk.pending, (state) => {
        state.updateUserPreferencesApiStatus.isLoading = true;
        state.updateUserPreferencesApiStatus.errorMessage = null;
        state.updateUserPreferencesApiStatus.successMessage = null;
        state.updateUserPreferencesApiStatus.isSuccess = false;
      })
      .addCase(updateUserPreferencesApiThunk.fulfilled, (state, action) => {
        state.updateUserPreferencesApiStatus.isLoading = false;
        state.updateUserPreferencesApiStatus.isSuccess = true;
        state.updateUserPreferencesApiStatus.successMessage = action.payload.message;
        state.userPreferences = action.payload.preferences;
      })
      .addCase(updateUserPreferencesApiThunk.rejected, (state, action) => {
        state.updateUserPreferencesApiStatus.isLoading = false;
        state.updateUserPreferencesApiStatus.isSuccess = false;
        state.updateUserPreferencesApiStatus.errorMessage = action.payload as string;
        state.updateUserPreferencesApiStatus.successMessage = null;
      });
  },
});

export const { clearUserPreferencesMessages, resetUpdateApiStatus } =
  userPreferencesSlice.actions;
export default userPreferencesSlice.reducer;
